import { verticalFadeIn } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { h3, paragraphLarge } from '@helpers/typography';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import SocialLinksStyles from '@stories/Components/Misc/SocialLinks/SocialLinks.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
const MotionWrapper = styled(m.div).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-in-view'] ? 'visible' : 'hidden',
    transition: {
        staggerChildren: 0.1,
    },
})) `
  display: contents;
`;
const Container = styled(SectionWrapper) `
  overflow: hidden;
  position: relative;

  @media ${until(Device.TabletLarge)} {
    ${SectionWrapperStyles.Inner} {
      --sectionSpacingTop: var(--sectionSpacing);
      --sectionSpacingBottom: 32px;

      @media ${from(Device.TabletLarge)} {
        --sectionSpacingBottom: var(--sectionSpacing);
      }
    }
  }

  ${SectionWrapperStyles.Inner} {
    display: flex;
    flex-direction: column;
  }
`;
const TopSection = styled.div `
  &[data-has-image='false'] {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    @media ${from(Device.TabletLarge)} {
      text-align: center;
    }
  }

  @media ${from(Device.TabletLarge)} {
    &[data-has-image='true'] {
      ${baseGrid};
    }
  }
`;
const Content = styled(m.div).attrs(() => ({
    variants: {
        visible: {
            transition: {
                staggerChildren: 0.1,
            },
        },
    },
})) `
  grid-column: 1 / span 5;

  [data-has-image='false'] & {
    max-width: 1032px;
  }

  display: flex;
  flex-direction: column;
  justify-content: center;

  z-index: 2;
`;
const BrandingLogo = styled(m.div).attrs(() => ({ variants: verticalFadeIn(15) })) `
  height: ${fluid(32, 50)};
  margin-bottom: ${fluid(16, 32)};

  svg,
  img {
    height: 100%;
    max-height: inherit;
  }
`;
const Date = styled(m.span).attrs(() => ({ variants: verticalFadeIn(15) })) `
  ${paragraphLarge}
  ${fonts.poppins.bold};

  margin-bottom: ${fluid(4, 8)};
`;
const Title = styled(m.h1).attrs(() => ({ variants: verticalFadeIn(15) })) `
  ${h3}
  margin-bottom: ${fluid(12, 32)};
`;
const Intro = styled(m.div).attrs(() => ({ variants: verticalFadeIn(15) })) `
  ${paragraphLarge}
  margin: 0;
`;
const ImageWrapper = styled(m.div).attrs(() => ({
    variants: {
        hidden: {
            opacity: 0,
            x: 25,
        },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.75,
                ease: 'easeOut',
            },
        },
    },
})) `
  --radius: ${fluid(16, 48)};

  display: flex;
  position: relative;
  z-index: 2;
  width: 100%;
  grid-column: 7 / -1;
  text-align: right;
  max-height: ${fluid(200, 495, Device.Mobile, Device.ActualDesktop)};
  width: calc(100% + max(var(--siteGutter), 0px) + var(--sitePadding));

  &::before {
    content: '';
    position: absolute;
    background-color: ${brand.primary.darkStone};
    z-index: 0;
    --offset: ${fluid(8, 16)};
    left: calc(var(--offset) * -1);
    top: var(--offset);
    height: 100%;
    width: calc(100% + var(--offset));
    border-radius: var(--radius);

    @media ${from(Device.TabletLarge)} {
      top: calc(var(--offset) * -1);
      height: calc(100% + (var(--offset) * 2));
      border-radius: calc(var(--radius) * 1.33) 0px 0px calc(var(--radius) * 1.33);
    }
  }

  img {
    width: 100%;
    object-fit: cover;
    object-position: center center;
    z-index: 1;
    border-radius: var(--radius);
    overflow: hidden;

    @media ${from(Device.TabletLarge)} {
      height: 100%;
      border-radius: var(--radius) 0px 0px var(--radius);
    }
  }

  @media ${until(Device.TabletLarge)} {
    margin-top: 32px;
    width: 100%;
  }
`;
const normalBackgroundIconCss = css `
  max-height: ${fluid(219, 414)};
  max-width: ${fluid(219, 414)};
  width: 100%;

  transform: rotate(-17deg) translate(35%, -30%);
  @media ${from(Device.TabletLarge)} {
    transform: rotate(-13.92deg) translate(-25%, -25%);
  }

  @media ${until(Device.TabletLarge)} {
    top: 0;
    right: 0;
  }
`;
const BackgroundIcon = styled(m.div).attrs(() => ({
    variants: {
        hidden: {
            opacity: 0,
        },
        visible: {
            opacity: 0.08,
            transition: {
                duration: 0.75,
                ease: 'easeOut',
            },
        },
    },
})) `
  position: absolute;
  z-index: 1;

  @media ${from(Device.TabletLarge)} {
    inset: 0;
  }

  [data-has-image='false'] & {
    @media ${until(Device.TabletLarge)} {
      ${normalBackgroundIconCss}
    }

    @media ${from(Device.TabletLarge)} {
      height: calc(100% + var(--sectionSpacingTop) + var(--sectionSpacingBottom));
      top: calc(-1 * var(--sectionSpacingTop));
      overflow: hidden;

      img {
        transform: scale(1.75);
      }
    }
  }

  [data-has-image='true'] & {
    ${normalBackgroundIconCss}
  }

  img {
    height: 100%;
  }
`;
const BottomSection = styled(SectionWrapper) `
  background-color: ${brand.primary.black};

  @media ${until(Device.TabletLarge)} {
    ${SectionWrapperStyles.Inner} {
      flex-direction: column;
      gap: 20px;
    }
  }

  ${SectionWrapperStyles.Inner} {
    padding-top: 16px;
    padding-bottom: 16px;

    display: flex;
    @media ${from(Device.TabletLarge)} {
      align-items: center;
      justify-content: space-between;
    }

    color: ${brand.primary.white};
  }
`;
const TextWithSection = styled(m.div).attrs(() => ({
    variants: {
        visible: {
            transition: {
                staggerChildren: 0.1,
            },
        },
    },
})) `
  display: flex;
  gap: ${fluid(4, 24)};
  align-items: center;

  &:has(${SocialLinksStyles.Container}) {
    gap: 16px;
  }

  &[data-stacked-mobile='true'] {
    @media ${until(Device.TabletLarge)} {
      align-items: unset;
      flex-direction: column;
    }
  }

  ${SocialLinksStyles.Item} {
    color: ${brand.primary.white};
  }
`;
const SectionText = styled(m.span).attrs(() => ({ variants: verticalFadeIn(15) })) `
  ${fonts.poppins.bold};
  font-size: ${fluid(14, 23)};
  line-height: ${fluid(17, 27)};

  &[data-show-pointer='true'] {
    cursor: pointer;
  }
`;
const Tags = styled(m.div).attrs(() => ({
    variants: {
        visible: {
            transition: {
                staggerChildren: 0.1,
            },
        },
    },
})) `
  display: flex;
  align-items: center;
  gap: ${fluid(8, 16)};

  @media ${until(Device.TabletLarge)} {
    overflow-x: auto;
    overflow-y: clip;
    position: relative;
    left: calc(-1 * var(--sitePadding));
    width: calc(100% + var(--sitePadding) * 2);
  }

  @media ${from(Device.TabletLarge)} {
    flex-wrap: wrap;
  }
  max-width: 934px;
`;
const Tag = styled(m.div).attrs(() => ({
    variants: {
        hidden: {
            opacity: 0,
            scale: 0.95,
        },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.5,
            },
        },
    },
})) `
  padding: ${fluid(7, 8)} ${fluid(8, 12)};
  background-color: ${brand.primary.white};
  color: ${brand.primary.black};
  border-radius: ${fluid(4, 8)};
  user-select: none;

  @media ${until(Device.TabletLarge)} {
    &:first-of-type {
      margin-left: var(--sitePadding);
    }

    &:last-of-type {
      margin-right: var(--sitePadding);
    }

    white-space: nowrap;
    min-width: max-content;
  }

  ${fonts.poppins.bold};
  font-size: ${fluid(13, 14)};
  line-height: ${fluid(16, 17)};
`;
const ShareLinksWrapper = styled(m.div).attrs(() => ({
    variants: verticalFadeIn(15),
})) ``;
export const ArticleBannerStyles = {
    Container,
    TopSection,
    Content,
    BrandingLogo,
    Date,
    Title,
    Intro,
    ImageWrapper,
    BackgroundIcon,
    BottomSection,
    TextWithSection,
    SectionText,
    Tags,
    Tag,
    MotionWrapper,
    ShareLinksWrapper,
};
